import type { CollectScenario } from '~/helpers/collectLeadTime'

export type CollectRow = {
  label: string
  message: string
  modalVariant?: 'mto' | 'resized'
  color?: string
  isError?: boolean
}

// Shared presentation for a resolved collect scenario. The bag/checkout tile and the bag edit
// panel must read identically (MHJ-258962 AC2), so both map their scenario through this.
export const useCollectRow = () => {
  const { t, locale } = useI18n()

  const buildCollectRow = (
    scenario: CollectScenario | null | undefined,
    context: { store?: SFCCStore; anchor?: Date; isResized?: boolean; standaloneError?: boolean },
  ): CollectRow | null => {
    if (!scenario) return null
    // Order lines date from placement so a revisit shows the promise made then.
    const anchor = context.anchor ?? new Date()

    if (scenario.kind === 'notAvailable') {
      return {
        label: '',
        // The tile pairs the short wording with its own error alert; a standalone row can't.
        message: stripTrailingPeriod(
          t(
            context.standaloneError
              ? 'product_details_page.find_store.not_available_at_store'
              : 'checkout.product_card.collect_not_available',
          ),
        ),
        color: AVAILABILITY_COLORS.OUT_OF_STOCK,
        isError: true,
      }
    }

    if (scenario.kind === 'storeStock') {
      if (!context.store) return null
      return {
        label: '',
        message: stripTrailingPeriod(t(getClickAndCollectMessageKey(context.store, anchor))),
        color: AVAILABILITY_COLORS.IN_STOCK,
      }
    }

    const label =
      scenario.reason === 'specialOrder'
        ? t('product.lead_time_callout.made_to_order')
        : context.isResized
          ? t('product.lead_time_callout.resized_for_you')
          : t('product.lead_time_callout.engraving')

    return {
      label: scenario.reason === 'standard' ? '' : label,
      modalVariant:
        scenario.reason === 'specialOrder' ? 'mto' : scenario.reason === 'resizeEngrave' ? 'resized' : undefined,
      color: scenario.reason === 'standard' ? AVAILABILITY_COLORS.IN_STOCK : undefined,
      message: stripTrailingPeriod(
        t('product_details_page.find_store.collect_by_date', {
          date: formatCollectionDate(addBusinessDays(anchor, scenario.leadDays), locale.value),
        }),
      ),
    }
  }

  return { buildCollectRow }
}
